import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  fetchServices, createService, updateService, toggleServiceStatus, deleteService,
} from "../services/serviceMasterService";

/**
 * Services master. The list is shared by the Manage Services screen and the
 * Employee form's services picker, so both read the same cache.
 */

const KEY = "services";

export const useServices = (activeOnly = false) =>
  useQuery({
    queryKey: [KEY, activeOnly ? "active" : "all"],
    queryFn: () => fetchServices(activeOnly),
    staleTime: 60 * 1000,
  });

/** Every write invalidates both variants — active-only included. */
const useServiceMutation = (mutationFn, successMessage) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      if (successMessage) toast.success(successMessage);
      queryClient.invalidateQueries({ queryKey: [KEY], refetchType: "all" });
    },
    // The service layer has already turned 403 / 404 / 422 into a readable line.
    onError: (err) => toast.error(err?.message || "Something went wrong"),
  });
};

export const useCreateService = () =>
  useServiceMutation((payload) => createService(payload), "Service saved");

export const useUpdateService = () =>
  useServiceMutation(({ id, ...payload }) => updateService(id, payload), "Service saved");

export const useToggleServiceStatus = () =>
  useServiceMutation((id) => toggleServiceStatus(id), null);

export const useDeleteService = () =>
  useServiceMutation((id) => deleteService(id), "Service deleted");
